import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Icon } from '@/shared/IconCollecion';
import useClickOutside from '@/shared/hooks/useClickOutside';
import type { WalletOption } from '../model/types';

// Вариант ProfitInfo для sidebar-меню (когда лого слева от заработка). Стили
// под светлый sidebar (content-secondary). Исходно был 1:1 с mcms-front;
// «Кошельки» с 31.07 — дропдаун со всеми кошельками, как в шапке serious
// (Header.tsx): строка ведёт на Финансы с этим кошельком в query.
//
// Живёт этот вариант в двух местах: сайдбар бренда с header='in-sidebar' и
// мобильная шторка обоих брендов.

type ProfitInfoMenuProps = {
  periodProfitAmount: string;
  userBalanceAmount: string;
  wallets: WalletOption[];
  // Переход по кошельку в мобильной шторке обязан её закрыть — иначе партнёр
  // остаётся под открытым навбаром с затемнением. Остальные ссылки шторки
  // гасят её сами (MenuItem, ссылка профиля); этот блок про шторку не знает,
  // поэтому закрытие приходит пропом. Десктопный сайдбар его не передаёт.
  onNavigate?: () => void;
  className?: string;
};

export const ProfitInfoMenu = ({
  periodProfitAmount,
  userBalanceAmount,
  wallets,
  onNavigate,
  className,
}: ProfitInfoMenuProps) => {
  const [open, setOpen] = useState(false);
  const walletsRef = useRef<HTMLDivElement>(null);
  useClickOutside(walletsRef, () => setOpen(false));

  const { t } = useTranslation('common');

  return (
    <div className={cn('flex flex-col gap-3 border-b border-line-muted pb-4 pl-[33px]', className)}>
      <div className="flex flex-col items-start">
        <span className="font-bold text-content-primary">{periodProfitAmount}</span>
        <span className="text-xs text-content-secondary">{t('profit.today')}</span>
      </div>

      <div ref={walletsRef} className="relative flex flex-col items-start">
        {/* Без кошельков дропдаун раскрывать нечем — остаётся прежний текст. */}
        {wallets.length === 0 ? (
          <>
            <span className="font-bold text-content-primary">{userBalanceAmount}</span>
            <span className="text-xs text-content-secondary">{t('profit.wallets')}</span>
          </>
        ) : (
          <button
            type="button"
            className="flex flex-col items-start text-left"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="font-bold text-content-primary">{userBalanceAmount}</span>
            <span className="flex items-center gap-1 text-xs text-content-secondary">
              {t('profit.wallets')}
              <Icon
                name="Caret_Down_Little"
                color="currentColor"
                className={cn('size-4 transition-transform', open && 'rotate-180')}
              />
            </span>
          </button>
        )}

        {open && (
          // Якорь справа, а не слева: оба места, где живёт menu-вариант, прижаты
          // к правому краю экрана — сайдбар wapclick (`layout.sidebar: 'right'`,
          // --aside-size 186px) и мобильная шторка (Drawer справа). От левого
          // края попап шириной 160px не влезал в доступные ~113px сайдбара и
          // вылезал за границу окна, давая горизонтальный скролл на FHD.
          <div className="absolute right-0 top-full z-50 mt-1 min-w-[160px] rounded-md border border-line-default bg-surface-card py-1 shadow-base">
            {wallets.map((w) => (
              <Link
                key={w.id}
                to={`/payments?wallet_id=${w.id}`}
                className="block px-3 py-2 text-sm font-normal leading-5 text-content-primary hover:bg-surface-hover"
                onClick={() => {
                  setOpen(false);
                  onNavigate?.();
                }}
              >
                {w.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
